import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import {
  SMART_WIZARD_FEATURE_IDS,
  SmartWizardFeatureId,
} from '../models/smart-wizard-feature.model';
import { OnboardingJourneyManagerService } from './onboarding-journey-manager.service';
import { UserExperienceLevelService } from './user-experience-level.service';
import { WIZARD_PROGRESS_STORAGE_KEYS } from '../constants/wizard-progress-storage-keys';

const STORAGE_PREFIX = 'smart_wizard_prefs_';
const STORAGE_VERSION_PREFIX = 'smart_wizard_prefs_version_';
const FINANCIAL_YEAR_ONBOARDING_DISMISSED_KEY = 'financial_year_onboarding_dismissed';
const MANDATORY_ONBOARDING_JOURNEY_PREFIX = 'mandatory_onboarding_journey_';
const OWNER_MANDATORY_SUSPENDED_PREFIX = 'smart_onboarding_owner_mandatory_flow_suspended_';
const STAGE4_EXIT_SUSPENDED_PREFIX = 'smart_onboarding_stage4_quickstart_exit_suspended_';

type PrefsMap = Partial<Record<SmartWizardFeatureId, boolean>>;

@Injectable({ providedIn: 'root' })
export class SmartWizardPreferencesService {
  private readonly auth = inject(AuthService);
  private readonly jwt = inject(JwtService);
  private readonly journey = inject(OnboardingJourneyManagerService);
  private readonly userExperience = inject(UserExperienceLevelService);

  private readonly prefsSignal = signal<PrefsMap>({});
  private activePrefsKey: string | null = null;

  /** يُزاد بعد مسح تقدم المعالجات لمزامنة الخدمات دون إعادة تحميل الصفحة. */
  readonly wizardProgressResetSeq = signal(0);

  /** يُحدَّث عند تغيير التفضيلات لربط computed الخارجية */
  readonly prefsVersion = computed(() => this.prefsSignal());

  constructor() {
    this.ensurePrefsSyncedWithCurrentUser();
  }

  isFeatureEnabled(id: SmartWizardFeatureId): boolean {
    this.ensurePrefsSyncedWithCurrentUser();
    const v = this.prefsSignal()[id];
    return v !== false;
  }

  setFeatureEnabled(id: SmartWizardFeatureId, enabled: boolean): void {
    this.ensurePrefsSyncedWithCurrentUser();
    const next: PrefsMap = { ...this.prefsSignal() };
    if (enabled) {
      delete next[id];
    } else {
      next[id] = false;
    }
    this.prefsSignal.set(next);
    this.persist(next);
  }

  enableAllStoredFeatures(options?: { resetProgress?: boolean }): void {
    this.ensurePrefsSyncedWithCurrentUser();
    this.prefsSignal.set({});
    this.persist({});
    if (options?.resetProgress) {
      this.resetAllWizardProgressForCurrentUser();
    }
  }

  disableAllStoredFeatures(): void {
    this.ensurePrefsSyncedWithCurrentUser();
    const next: PrefsMap = {};
    for (const id of SMART_WIZARD_FEATURE_IDS) {
      next[id] = false;
    }
    this.prefsSignal.set(next);
    this.persist(next);
  }

  resetAllWizardProgressForCurrentUser(): void {
    try {
      for (const key of WIZARD_PROGRESS_STORAGE_KEYS) {
        localStorage.removeItem(key);
      }
      localStorage.removeItem(FINANCIAL_YEAR_ONBOARDING_DISMISSED_KEY);

      const token = this.auth.getAccessToken();
      const userId = token ? this.jwt.getUserId(token) : null;
      if (userId) {
        localStorage.removeItem(`${MANDATORY_ONBOARDING_JOURNEY_PREFIX}${userId}`);
        localStorage.removeItem(`${OWNER_MANDATORY_SUSPENDED_PREFIX}${userId}`);
        localStorage.removeItem(`${STAGE4_EXIT_SUSPENDED_PREFIX}${userId}`);
      }

      this.userExperience.reloadSuspendedFlagsFromStorage();
      this.journey.syncJourneyStateAfterWizardStorageReset();
      this.refreshWizardProgressBindingsFromStorage();
    } catch {
      /* ignore */
    }
  }

  /** إعادة ربط إشارات تقدم المعالجات من localStorage (مثلاً بعد تسجيل الدخول دون إعادة تحميل الصفحة). */
  refreshWizardProgressBindingsFromStorage(): void {
    this.wizardProgressResetSeq.update((v) => v + 1);
  }

  private persist(map: PrefsMap): void {
    try {
      const key = this.prefsKey();
      if (!key) return;
      // Always write a concrete value so changes are visible and deterministic.
      localStorage.setItem(key, JSON.stringify(map));
      const versionKey = this.versionKey();
      if (versionKey) {
        localStorage.setItem(versionKey, String(Date.now()));
      }
    } catch {
      /* ignore */
    }
  }

  private loadPrefs(key: string | null): PrefsMap {
    try {
      if (!key) return {};
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as PrefsMap;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private prefsKey(): string | null {
    const token = this.auth.getAccessToken();
    if (!token) return null;
    const userId = this.jwt.getUserId(token);
    if (!userId) return null;
    return `${STORAGE_PREFIX}${userId}`;
  }

  private versionKey(): string | null {
    const token = this.auth.getAccessToken();
    if (!token) return null;
    const userId = this.jwt.getUserId(token);
    if (!userId) return null;
    return `${STORAGE_VERSION_PREFIX}${userId}`;
  }

  private ensurePrefsSyncedWithCurrentUser(): void {
    const key = this.prefsKey();
    if (key === this.activePrefsKey) {
      return;
    }
    this.activePrefsKey = key;
    this.prefsSignal.set(this.loadPrefs(key));
  }
}
